import { Checkbox, Grid, FormControlLabel } from '@mui/material';
import { CheckBoxOutlined } from '@mui/icons-material';
import styles from './DisclaimerAgreement.module.scss';
import { Button, Modal } from 'tt-ui-lib/core';
import PartialLoader from 'components/layout/PartialLoader';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Chart from 'components/Charts/RadialChart/Chart';
import { useSelectedContext } from 'context/contextSelector';
import { URL_CALCS_DRAFTS } from 'constants/calculatorsPage';
import { CALC_TYPES } from 'utils/calculatorUtils';
import { getCalcDraftById, preliminaryAssessment } from 'api/calc/endpoints/calc';

const DisclaimerAgreement = ({ cost, isModalOpen, setIsModalOpen, confirmChange }) => {
  const navigate = useNavigate();

  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [draft, setDraft] = useState();
  const [score, setScore] = useState({});
  const windowWidth = useRef(window.innerWidth);
  const [isDataLoaded, setIsDataLoaded] = useState({});
  const { user } = useSelectedContext((context) => context.user);

  const getDraft = () => {
    getCalcDraftById({ id: cost.draftId })
      .then((res) => {
        const { data, success } = res.data;
        console.log('getDraft data', data);
        if (success) {
          setDraft(data);
        }
      })
      .finally(() => {
        setIsDataLoaded((val) => ({ ...val, draft: true }));
      });
  };

  const loadResult = () => {
    preliminaryAssessment({ id: cost.draftId })
      .then((res) => {
        const { data, success } = res.data;
        console.log('loadResult data', data);
        if (success) {
          const value = data.global ?? data.local ?? data.carbon;
          setScore({
            total: Math.round(value * 100) / 100,
            carbon: data.carbon ?? 0,
            global: data.global ?? 0,
            local: data.local ?? 0,
            testYear: new Date(data.reportDate).getFullYear(),
          });
        }
      })
      .finally(() => {
        setIsDataLoaded((val) => ({ ...val, result: true }));
      });
  };

  useEffect(() => {
    getDraft();
    loadResult();

    return () => {
      setIsDataLoaded({});
      setIsAgreed(false);
    };
  }, []);

  const navigateToDrafts = () => {
    navigate(URL_CALCS_DRAFTS);
  };

  const scrolledToEnd = (e) => {
    const { target } = e;
    setIsCheckboxEnabled(
      target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight * 0.95
    );
  };

  const getTotal = () =>
    Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
      score.total
    );

  return (
    <Modal
      open={isModalOpen}
      onClick={(e) => e.stopPropagation()}
      onClose={() => setIsModalOpen(false)}
      title="Release and Send to the Blockchain"
      fullScreenOnResponse
      closeOnlyByControls
      closable={false}
    >
      <div className={styles.container}>
        <div className={styles.chartArea}>
          <Chart
            chartData={(draft && draft.stateGraphics) ?? []}
            score={score.total ? score.total.toString() : ''}
            hasDesktopFormat
          />
        </div>
        <div className={styles.textArea}>
          <div className={styles.owerflowContainer} onScroll={scrolledToEnd}>
            <div>
              {cost.calcType === CALC_TYPES.LOCAL && (
                <>
                  {`Based on your self-assessment, your ESG Excellence score for the
                    year ${score.testYear} is ${score.total}.`}
                  <br />
                </>
              )}
              {(cost.calcType === CALC_TYPES.CARBON ||
                cost.calcType === CALC_TYPES.LOCAL ||
                cost.calcType === CALC_TYPES.PROJECT) && (
                <>
                  <p>You have completed your calculations.</p>
                  <p>The carbon footprint for year {score.testYear} is:</p>
                  <p>Company carbon footprint {score.companyTotal}.</p>
                </>
              )}
              {cost.calcType === CALC_TYPES.GLOBAL && (
                <>
                  <p>You have completed your calculations.</p>
                  <p>
                    The ESG Benchmark result for year {score.testYear} is:
                    {score.globalTotal}.
                  </p>
                </>
              )}
              <div>
                <br />
                <p>
                  <strong>
                    {`Your self-assessment results will be encrypted, and the hash
                      will be deployed and notarized on the FCE Blockchain.It is a
                      paid option. Please proceed with payment to get access to your
                      assessment.`}
                  </strong>
                </p>
                <br />
              </div>
              <h3>Disclaimer</h3>
              <br />
              <div>
                <p>
                  {`To become visible to interested investors, you need to submit
                    the data to the blockchain.`}
                </p>
                <p>
                  {`The data will be sent to FCE Group's blockchain via the
                    TransparenTerra platform.`}
                </p>
                <p>
                  {`We will not send raw data to the blockchain, only data hashes.
                    All your data will be stored on the profile page, hashed there
                    and sent to the blockchain in encrypted form. If someone wants
                    to compare or verify the data on the blockchain, they will
                    compare the hashes, not the data itself. The hash is generated
                    on the profile page.`}
                </p>
                <p>
                  {`The responsibility for the quality of the data lies solely with
                    those who enter the data. The data in the blockchain is
                    immutable and will be stored forever. To respect your rights
                    under GDPR, we will send hashes of your ESG score results there.
                    In your profile, experts, investors and any interested parties
                    who have access to this data can compare the validity of your
                    data with the hashes.`}
                </p>
                <p>
                  {`The results sent to the blockchain contribute to the full
                    transparency of your company and become attractive for
                    investment.`}
                </p>
                <p>
                  {`All blockchain transactions are public and chargeable. Once
                    published, the data cannot be deleted or changed, but you can
                    always complete a new calculation and publish the updated
                    results.`}
                </p>
              </div>
              <br />
            </div>
          </div>
          <div className={styles.agreeCheck}>
            <div className={styles.checkboxContainer}>
              <FormControlLabel
                className={styles.checkbox}
                control={
                  <Checkbox
                    onChange={() => setIsAgreed((value) => !value)}
                    checked={isAgreed}
                    checkedIcon={<CheckBoxOutlined />}
                    variant="unlighted"
                    disabled={windowWidth.current >= 1024 && !isCheckboxEnabled && !isAgreed}
                  />
                }
                label="I have read and agree"
                labelPlacement="end"
              />
            </div>
          </div>
        </div>
        <PartialLoader loading={!isDataLoaded.draft || !isDataLoaded.result} isLightContainer />
      </div>
      <div className={styles.buttonCont}>
        <Button onClick={navigateToDrafts}>Go to my drafts</Button>
        <Button type="primary" onClick={() => confirmChange()} disabled={!isAgreed}>
          Release
        </Button>
      </div>
    </Modal>
  );
};

export default DisclaimerAgreement;
